/**
 * Shared AG Grid cell renderer components.
 * Import from here instead of defining them locally per-page.
 */

import DOMPurify from "dompurify";
import { memo } from "react";
import { BOQ_STATUS } from "../../../constants/boq-status";
import "./grid-cell-renderers.scss";
import {
    getStatusColor,
    getStatusTranslationKey,
} from "../../../constants/lv-status";

/**
 * Renders raw HTML content inside an AG Grid cell.
 * Use this whenever a `valueGetter` returns an HTML string.
 *
 * @param {{ value: string }} props
 */
export const HtmlCellRenderer = memo(({ value }) => (
    <div
        className="gcr-html-cell"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
    />
));
HtmlCellRenderer.displayName = "HtmlCellRenderer";

/**
 * Status cell for the project-LV grid.
 * Shows:
 *  - Nachtrage status badge (clickable inline-edit for local LVs without an invoice)
 *  - BoQ status badge when the BoQ Editor has been used (clickable → navigate to BoQ editor)
 *
 * Pass via `cellRendererParams`:
 *   - `onEditClick(data)`      - opens the edit-LV modal
 *   - `onOpenBoQEditor(data)`  - navigates to the BoQ editor
 *   - `canEdit`                - whether the current user has LV EDIT permission
 *   - `t`                      - i18next translate function
 */
export const StatusCellRenderer = memo(({ data, onEditClick, onOpenBoQEditor, canEdit = false, t }) => {
    if (!data?.isNachtraege) return null;

    const { statusCode, boqStatus, isLocal, hasInvoice } = data;
    if (!statusCode && !boqStatus) return null;

    const statusColor = statusCode ? getStatusColor(statusCode) : null;
    const statusText = statusCode
        ? t(getStatusTranslationKey(statusCode), { defaultValue: statusCode })
        : null;

    // Local NAs without an invoice can be edited inline - only when user has LV EDIT permission
    const canInlineEdit = isLocal && !hasInvoice && canEdit;

    return (
        <div className="gcr-status-cell">
            {statusCode && (
                <div className={boqStatus ? "mb-1" : ""}>
                    <span
                        className={`gcr-status-badge${canInlineEdit ? " cursor-pointer" : ""}`}
                        style={{ backgroundColor: statusColor }}
                        onClick={canInlineEdit ? () => onEditClick(data) : undefined}
                        title={canInlineEdit ? t("lv.click-to-edit") : undefined}
                    >
                        {statusText}
                        {canInlineEdit && (
                            <span className="gcr-edit-hint">✎</span>
                        )}
                    </span>
                </div>
            )}
            {boqStatus && (
                <div>
                    <span
                        className={`gcr-boq-badge gcr-boq-badge--${boqStatus === BOQ_STATUS.FINALIZED ? "finalized" : "draft"}${canEdit ? " cursor-pointer" : ""}`}
                        onClick={canEdit ? () => onOpenBoQEditor(data) : undefined}
                        title={canEdit ? t("lv.click-to-open-boq-editor") : undefined}
                    >
                        {boqStatus === BOQ_STATUS.FINALIZED
                            ? `BoQ: ${t("boq.toolbar.finalized")}`
                            : `BoQ: ${t("boq.toolbar.draft")}`}
                    </span>
                </div>
            )}
        </div>
    );
});
StatusCellRenderer.displayName = "StatusCellRenderer";
